import { History } from '@orgus/ui/icons';
import { useMemo } from 'react';
import type { Pipeline, PipelineItem } from '@orgus/protocol';
import { EmptyState, RelativeTime, formatDuration, formatInt } from '@orgus/ui';
import { ResultColumns, ResultRow, resultStatus } from './ResultRow.tsx';
import { attemptOf, pipelineChains } from './chains.ts';
import { commitTitle, failureSummary, finishedNote, humanTitle, matchesFilter } from './present.ts';

/** The last finished items, newest first, one line each; a failure as its short status and what failed. */
export function RecentList({ pipeline: p, onOpen, query = '' }: { pipeline: Pipeline; onOpen: (i: PipelineItem) => void; query?: string }) {
  const list = useMemo(
    () => [...p.recent].filter((i) => matchesFilter(i, p, { state: null, query })).sort((a, b) => (b.endedAt ?? 0) - (a.endedAt ?? 0)),
    [p, query],
  );
  const chains = useMemo(() => pipelineChains(p), [p]);
  if (!list.length) {
    if (query.trim()) return <p className="px-4 py-8 text-center text-ui text-fg-subtle">No change matches "{query.trim()}"</p>;
    return <EmptyState icon={<History />} title="Nothing has finished yet" description="Passed, failed and withdrawn items list here." />;
  }
  return (
    <div className="flex flex-col px-1 pb-1">
      <ResultColumns first="Duration" last="Ended" />
      <ol data-list="" className="flex flex-col">
        {list.map((i) => {
          const attempt = attemptOf(chains, i.id);
          const t = commitTitle(humanTitle(i).title);
          const failure = i.state === 'failed' ? failureSummary(i, p) : null;
          const detail = [attempt && attempt.n > 1 ? `Attempt ${formatInt(attempt.n)}` : null, failure ? failure.reason : finishedNote(i), i.by].filter(Boolean).join(', ');
          const took = i.startedAt != null && i.endedAt != null ? formatDuration(i.endedAt - i.startedAt) : '';
          return (
            <ResultRow
              key={i.id}
              status={resultStatus(i, p)}
              title={t.subject}
              fullTitle={i.title}
              type={t.type}
              detail={detail}
              failure={failure?.label}
              first={took}
              last={<RelativeTime ts={i.endedAt ?? null} fallback="" />}
              onOpen={() => onOpen(i)}
            />
          );
        })}
      </ol>
    </div>
  );
}
